import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faInfoCircle, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { centToDollar } from "../../../utils/stringFormatter";

import RentParkingForm from './RentParkingForm';
import StepCard from './StepCard';
import Tooltip from '../../../components/Tooltip';
import i18n from '../../../i18n/i18n';
import LazyImage from '../../../components/LazyImage';

import HotBadge from "../../../images/mapSearch/hot-badge.png";
import EvBadge from "../../../images/mapSearch/ev-badge.png";
import ParkingPlaceholderImg from "../../../images/parking-icon-placeholder.png";
import WalkingIcon from "../../../images/mapSearch/walking-icon.svg";
import FloatingIcon from "../../../images/mapSearch/floating-graphic.svg";
import ReservedIcon from "../../../images/mapSearch/reserved-graphic.svg";
import ResidentialIcon from "../../../images/mapSearch/residential-graphic.svg";
import CommercialIcon from "../../../images/mapSearch/commercial-graphic.svg";

import AppointmentIcon from "../../../images/rentParking/appointment.png";
import BookNowIcon from "../../../images/rentParking/book-now.png";
import CollectAccessCardIcon from "../../../images/rentParking/collect-access-card.png";
import ConfirmIcon from "../../../images/rentParking/confirm.png";
import GetInTouchIcon from "../../../images/rentParking/get-in-touch.png";
import PaymentIcon from "../../../images/rentParking/payment.png";

const RentParkingModal = ({
  isModalOpen,
  parkingArea,
  distance,
  onCloseModal,
  onShowShareModal
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  if (!parkingArea) {
    return null;
  }

  const price = parkingArea?.displayMonthlyPrice?.avg ? centToDollar(parkingArea?.displayMonthlyPrice?.avg) : 'N/A';
  const availableBay = (parkingArea.additionalInformation.totalVacant ?? 0);

  return (
    <div tabIndex="-1" aria-hidden="true" className={`fixed top-0 left-0 right-0 bottom-0 z-20 w-full p-4 overflow-x-hidden overflow-y-auto max-h-screen ${isModalOpen ? '' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen min-w-[340px] max-w-[80%] md:max-w-[500px] mx-auto">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-900 opacity-75" />
        </div>
        <div className="relative h-[calc(85vh-1rem)] m-2 w-full bg-white shadow-lg rounded-[10px] overflow-y-scroll ">
          <div className="relative h-[200px] w-full bg-center bg-cover bg-no-repeat border-tr-20">
            <div className="flex flex-row absolute m-[20px] z-10">
              {parkingArea.additionalInformation.hasEvCharging && (
                <Tooltip placement="right" tooltip={i18n.t("findParking.evCharging")}>
                  <LazyImage className="w-[50px] mr-[8px]" src={EvBadge} alt="ev-icon" />
                </Tooltip>
              )}
              {parkingArea.additionalInformation.isHot && (
                <Tooltip placement="right" tooltip={i18n.t("findParking.highDemand")}>
                  <LazyImage className="w-[50px]" src={HotBadge} alt="hot-icon" />
                </Tooltip>
              )}
            </div>
            <button type="button" onClick={onCloseModal} className="absolute top-3 right-2.5 text-primary900 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 z-20 ml-auto inline-flex justify-center items-center">
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
            <div className="h-full w-full absolute top-0 left-0 z-0 bg-center bg-cover opacity-25" style={{ backgroundImage: `url(${parkingArea?.additionalInformation?.images?.length > 0 ? parkingArea.additionalInformation.images[0].url : ParkingPlaceholderImg})` }}></div>
            <div className="h-full w-full absolute top-0 left-0 z-0 bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url(${parkingArea?.additionalInformation?.images?.length > 0 ? parkingArea.additionalInformation.images[0].url : ParkingPlaceholderImg})` }}></div>
          </div>
          <div className="flex flex-col p-5">
            <button type="button" onClick={onShowShareModal} className="absolute right-4 text-[#929292] bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 z-20 ml-auto inline-flex justify-center items-center">
              <FontAwesomeIcon className="w-[18px] h-[18px]" icon={faShareNodes} />
            </button>
            <h4 className="text-[18px] mb-1 font-bold">{parkingArea.name}</h4>
            <div className="flex flex-wrap gap-1 items-end my-[5px]">
              <span className="text-black text-[11px] leading-3">{i18n.t("findParking.price")}</span>
              <span className="font-bold text-black leading-3">{`${parkingArea.currency ?? 'MYR'} ${price}`}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                {parkingArea.additionalInformation.totalVacant > 0 ? (
                  <p className="mb-[5px] text-[11px]">{`${availableBay} ${i18n.t("findParking.availableBay")}`}</p>
                ) : (
                  <div className="flex items-center">
                    <p className="mr-[5px] text-[#0d59bd] text-[11px] font-medium sm:text-[12px]">
                      {i18n.t("findParking.fullyBooked")}
                    </p>
                    <div className="text-[#0d59bd]">
                      <Tooltip name={parkingArea.id} placement="top" trigger="hover" tooltip={i18n.t("findParking.waitingList")}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Tooltip>
                    </div>
                  </div>
                )}
                {distance && distance.status === "OK" && (
                  <div className="flex gap-2 mt-1 items-center pointer-events-none">
                    <img className="w-[15px]" src={WalkingIcon} alt="walkingicon" />
                    <div>
                      <p className="text-[10px] text-[#929292]">{i18n.t("findParking.carToProperty")}</p>
                      <p className="text-[12px] font-medium">{distance.duration.text} &nbsp; &nbsp; {distance.distance.text}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center gap-2 my-5">
              <button onClick={() => setSelectedTab(0)} className={`border rounded-[18px] py-2 w-full text-[11px] ${selectedTab === 0 ? 'bg-primary900 text-white' : ''}`}>{i18n.t("findParking.overview")}</button>
              <button onClick={() => setSelectedTab(1)} className={`border rounded-[18px] py-2 w-full text-[11px] ${selectedTab === 1 ? 'bg-primary900 text-white' : ''}`}>{i18n.t("findParking.howToBook")}</button>
              <button onClick={() => setSelectedTab(2)} className={`border rounded-[18px] py-2 w-full text-[11px] ${selectedTab === 2 ? 'bg-primary900 text-white' : ''}`}>{i18n.t("findParking.howToPark")}</button>
            </div>
            {selectedTab === 0 && (
              <div>
                <div className="flex justify-evenly items-center gap-2 py-3 mb-[15px] border-b border-gray-200">
                  {parkingArea.additionalInformation?.hasReserved && (
                    <div className="flex flex-col text-center gap-1">
                      <img src={ReservedIcon} className="h-[34px] object-contain" alt='reserved' />
                      <span className="text-[11px]">{i18n.t("findParking.carparkType.reserved")}</span>
                    </div>
                  )}
                  {parkingArea.additionalInformation?.hasFloating && (
                    <div className="flex flex-col text-center gap-1">
                      <img src={FloatingIcon} className="h-[34px] object-contain" alt='floating' />
                      <span className="text-[11px]">{i18n.t("findParking.carparkType.floating")}</span>
                    </div>
                  )}
                  {parkingArea.additionalInformation?.hasCommercial && (
                    <div className="flex flex-col text-center gap-1">
                      <img src={CommercialIcon} className="h-[34px] object-contain" alt='commercial' />
                      <span className="text-[11px]">{i18n.t("findParking.carparkType.commercial")}</span>
                    </div>
                  )}
                  {(parkingArea.additionalInformation?.hasApartment || parkingArea.additionalInformation?.hasLanded) && (
                    <div className="flex flex-col text-center gap-1">
                      <img src={ResidentialIcon} className="h-[34px] object-contain" alt='residential' />
                      <span className="text-[11px]">{i18n.t("findParking.carparkType.residential")}</span>
                    </div>
                  )}
                </div>
                <RentParkingForm parkingArea={parkingArea} />
              </div>
            )}

            {selectedTab === 1 && (
              <div className="flex flex-col gap-[18px] mb-10">
                <StepCard icon={BookNowIcon}>
                  <p>{i18n.t("findParking.book.step1A")}</p>
                  <p>{i18n.t("findParking.book.step1B")}</p>
                </StepCard>
                <StepCard icon={PaymentIcon}>
                  <p>{i18n.t("findParking.book.step2")}</p>
                </StepCard>
                <StepCard icon={GetInTouchIcon}>
                  <p>{i18n.t("findParking.book.step3")}</p>
                </StepCard>
              </div>
            )}

            {selectedTab === 2 && (
              <div className="flex flex-col gap-[18px] mb-10">
                <StepCard icon={ConfirmIcon}>
                  <p>{i18n.t("findParking.park.step1")}</p>
                </StepCard>
                <StepCard icon={AppointmentIcon}>
                  <p>{i18n.t("findParking.park.step2")}</p>
                </StepCard>
                <StepCard icon={CollectAccessCardIcon}>
                  <p>{i18n.t("findParking.park.step3")}</p>
                </StepCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RentParkingModal;
